import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so multiple components/pages don't all re-fetch the same
// version string back-to-back. The first caller pays the round-trip; the rest
// resolve instantly.
let _cached = null;
let _cachedAt = 0;
const TTL_MS = 60 * 1000; // 60s

/**
 * useAppVersion — returns the currently published app version info (e.g.
 * ``{ version: "26.07.02", publishedAt: "2026-07-27T..." }``) from
 * ``GET /api/system/version``. Refreshes on mount and on tab focus, so the
 * footer always reflects what an admin most recently published — no redeploy
 * needed.
 *
 * Returns ``{ version, publishedAt, loading }`` where ``version`` /
 * ``publishedAt`` may be ``null`` while ``loading`` is ``true`` on the very
 * first mount (unless a cached value is available), letting the caller render
 * a skeleton instead of a fallback value that will visibly "flip" once the
 * network resolves.
 */
export default function useAppVersion() {
  const [info, setInfo] = useState(() => (
    _cached
      ? { ..._cached, loading: false }
      : { version: null, publishedAt: null, loading: true }
  ));

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        // Honour the short cache when navigating between pages.
        if (_cached && Date.now() - _cachedAt < TTL_MS) {
          if (alive) setInfo({ ..._cached, loading: false });
          return;
        }
        const res = await axios.get(`${API}/system/version?_=${Date.now()}`, {
          timeout: 8000,
          headers: { 'Cache-Control': 'no-cache' },
        });
        const next = {
          version: res.data?.version || null,
          publishedAt: res.data?.published_at || null,
        };
        _cached = next;
        _cachedAt = Date.now();
        if (alive) setInfo({ ...next, loading: false });
      } catch (err) {
        // Silent — mark loading complete so callers stop showing skeletons.
        if (alive) setInfo((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchOnce();
    const onVis = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return info;
}
